import { Controller } from '@hotwired/stimulus';

/**
 * Multi-Token Checkbox Controller
 *
 * Ties ONE visible identity checkbox to N hidden participation-token inputs
 * (#556 — CR attendee picker multi-casquette). The checkbox itself carries no
 * `name`/`value`; checking it ENABLES its sibling hidden `token` inputs (so
 * they are included in the form POST), unchecking it DISABLES them (a disabled
 * input is never submitted). This lets a single row — a multi-casquette
 * intervenant carrying several `ProjectActor` tokens — submit every token at
 * once while still counting as ONE row for the `checkbox-group` counter.
 *
 * SERVER-RENDERED DEFAULT IS `disabled` (never JS-only): the `token` inputs
 * ship the `disabled` attribute directly in the ERB template, so a
 * before-connect window or a no-JS submit sends NO tokens for an unchecked
 * (default) row. This controller only ENABLES tokens once the user actively
 * checks the row — it never widens what a no-JS submit would send.
 *
 * This controller is mounted ONLY on a SUBMITTABLE row (#556): a row already on
 * the CR renders its checkbox `checked` AND `disabled` with NO tokens and NO
 * `multi-token-checkbox` at all (an insubmittable row carries nothing to sync).
 * `sync()` is therefore hardened to treat a `disabled` toggle as unchecked — a
 * belt-and-braces guard so this controller can never re-enable tokens on a
 * disabled row, should one ever mount it by mistake.
 *
 * POST-REPLACE TOKEN RECONCILIATION (`tokenTargetConnected`): editing an
 * intervenant's casquettes rebuilds its `ProjectActor` rows, so the participation
 * tokens change identity. A Turbo stream replaces ONLY the row's `…-tokens`
 * sub-container with fresh tokens (the `toggle` checkbox is never detached — it
 * stays in the DOM with its `checked` state intact). Those fresh tokens ship the
 * server `disabled` default; without reconciliation a row the user had checked
 * would silently drop out of the POST. `tokenTargetConnected` re-runs `sync()`
 * against the surviving checkbox so re-rendered tokens inherit the CURRENT
 * checked state.
 *
 * Targets:
 * - toggle: The visible identity checkbox (REQUIRED).
 * - token: Hidden inputs carrying the participation tokens, disabled/enabled
 *   in lockstep with `toggle` (one or more).
 *
 * Actions:
 * - sync: Recalculates the `disabled` state of every `token` target from the
 *   current `toggle` checked state. Call on connect AND on the checkbox's
 *   `change` event.
 *
 * This controller adds NO listeners outside `this.element`, so there is
 * nothing to tear down in `disconnect()`.
 *
 * @example
 * <label data-controller="multi-token-checkbox">
 *   <input type="checkbox" data-multi-token-checkbox-target="toggle"
 *          data-action="change->multi-token-checkbox#sync">
 *   <input type="hidden" name="carnet_actor_ids[]" value="actor:1" disabled
 *          data-multi-token-checkbox-target="token">
 *   <input type="hidden" name="carnet_actor_ids[]" value="actor:2" disabled
 *          data-multi-token-checkbox-target="token">
 * </label>
 */
export default class extends Controller {
  static targets = ['toggle', 'token'];

  connect() {
    // Re-syncs to `toggle`'s initial state — a no-op against the server
    // default (unchecked → tokens stay disabled), and the enabling leg for a
    // Turbo-cached / back-forward restored row that reconnects pre-checked.
    this.sync();
  }

  // A Turbo stream can replace this row's `…-tokens` sub-container with fresh,
  // server-`disabled` tokens after a casquette edit (the `toggle` checkbox is
  // never detached). Reconcile the new tokens with the CURRENT checked state so
  // a row the user had checked does not silently drop out of the POST.
  tokenTargetConnected() {
    this.sync();
  }

  /**
   * Enables every `token` target when `toggle` is checked (including them in
   * the POST body), disables them when it is unchecked — restoring the
   * server-rendered default. A `disabled` toggle counts as unchecked (a disabled
   * checkbox can never emit `change`, so this only hardens the mount path).
   */
  sync() {
    const checked =
      this.hasToggleTarget && this.toggleTarget.checked && !this.toggleTarget.disabled;

    this.tokenTargets.forEach((input) => {
      input.disabled = !checked;
    });
  }
}
